import { useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { consumeOAuthRedirectState } from '../auth/oauthRedirectState';
import { resolveLogoutTransitionTarget } from '../auth/logoutTransition';
import { useAppSelector } from '../state/hooks';
import { useShellPersistence } from '../state/useShellPersistence';
import { useShellProfile } from '../state/useShellProfile';
import { resolvePostAuthPersonaSelectionMode } from '../config/publicAuth';
import { AuthTransitionScreen } from './routes/AuthTransitionScreen';
import {
  AuthFailureRouteScreen,
  AuthGate,
  AuthRoute,
  PostGooglePersonaSelectionScreen,
} from './routes/AuthRoutes';
import { resolveRootEntryRenderState } from './routes/authRenderState';
import {
  PrivacyPolicyRoute,
  PublicRoute,
  TermsConditionsRoute,
} from './routes/PublicRoutes';
import { ShellLayoutRoute } from './routes/ShellLayoutRoute';
import { isModuleId, isSafeInternalPath, resolvePostAuthTarget } from './routePolicy';

export const RootEntryRoute = () => {
  const {
    authStatus,
    effectivePersona,
    profile,
    personaProvisioningStatus,
    staffAccessPendingApproval,
    personaTokenReady,
  } = useShellProfile();
  const location = useLocation();
  const shellState = useAppSelector((state) => state.shell);
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect_to');
  const returnTo = params.get('returnTo');
  const defaultModuleParam = params.get('defaultModule');
  const pendingOauthStateRef = useRef(consumeOAuthRedirectState());
  const pendingOauthState = pendingOauthStateRef.current;
  const pendingReturnTo = pendingOauthState?.returnTo ?? null;
  const pendingDefaultModule = pendingOauthState?.defaultModule ?? null;
  const personaSelectionMode = resolvePostAuthPersonaSelectionMode({
    isGoogleProvider: profile?.isGoogleProvider,
    isIntellioUser: profile?.isIntellioUser,
    groups: profile?.groups,
  });
  const personaSelectionAllowed = personaSelectionMode !== 'disabled';
  const postAuthSelectionRequested = Boolean(
    pendingOauthState?.postAuthPersonaSelection && personaSelectionAllowed,
  );
  const queryTarget = redirectTo ?? returnTo ?? pendingReturnTo ?? null;
  const defaultModule = isModuleId(defaultModuleParam)
    ? defaultModuleParam
    : isModuleId(pendingDefaultModule)
      ? pendingDefaultModule
      : null;
  const logoutTarget = resolveLogoutTransitionTarget();
  const renderState = resolveRootEntryRenderState({
    authStatus,
    logoutTarget,
    personaProvisioningStatus,
    effectivePersona,
    personaTokenReady,
    postAuthPersonaSelection: postAuthSelectionRequested,
    personaSelectionAllowed,
  });

  if (authStatus === 'authenticated' && staffAccessPendingApproval) {
    return <AuthFailureRouteScreen testId="root-entry-staff-pending-screen" />;
  }

  switch (renderState) {
    case 'failure':
      return <AuthFailureRouteScreen testId="root-entry-failure-screen" />;
    case 'persona_selection':
      return (
        <PostGooglePersonaSelectionScreen
          testId="root-entry-post-google-persona-selection-screen"
          returnTo={isSafeInternalPath(queryTarget) ? queryTarget : null}
          defaultModule={defaultModule}
          schoolId={pendingOauthState?.schoolId ?? null}
          selectionMode={personaSelectionMode}
        />
      );
    case 'persona_provisioning':
      return (
        <AuthTransitionScreen
          testId="root-entry-persona-provisioning-screen"
          title="Preparing access"
          description="Finishing persona provisioning for this session."
        />
      );
    case 'token_refresh':
      return (
        <AuthTransitionScreen
          testId="root-entry-token-refresh-screen"
          title="Refreshing access"
          description="Waiting for role claims to become available."
        />
      );
    case 'redirect': {
      if (effectivePersona === 'guest' && profile) {
        return (
          <AuthFailureRouteScreen
            testId="root-entry-unsupported-persona-screen"
            title="Access not available"
          />
        );
      }
      const target = resolvePostAuthTarget({
        persona: effectivePersona,
        lastVisitedPath: shellState.lastVisitedPath,
        defaultModule: defaultModule ?? shellState.defaultModule,
        returnTo: isSafeInternalPath(queryTarget) ? queryTarget : null,
        authGroups: profile?.groups,
      });
      return <Navigate to={target} replace />;
    }
    case 'logout_redirect':
      return <Navigate to={logoutTarget ?? '/auth'} replace />;
    case 'configuring':
      return (
        <AuthTransitionScreen
          testId="root-entry-configuring-screen"
          title="Preparing access"
          description="Waiting for authentication state to settle."
        />
      );
    case 'apply':
    default:
      return <Navigate to="/apply" replace />;
  }
};

export const AppRoutes = () => {
  const { profile } = useShellProfile();
  useShellPersistence(profile?.userSub ?? null);

  return (
    <Routes>
      <Route path="/apply" element={<PublicRoute />} />
      <Route path="/redirect" element={<Navigate to="/apply" replace />} />
      <Route path="/auth" element={<AuthRoute />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
      <Route path="/terms-conditions" element={<TermsConditionsRoute />} />
      <Route path="/" element={<RootEntryRoute />} />
      <Route
        path="/*"
        element={(
          <AuthGate>
            <ShellLayoutRoute />
          </AuthGate>
        )}
      />
    </Routes>
  );
};
